import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getIncidents, getTenants } from '../../services';
import type { EnrichedIncident, Tenant } from '../../data/types';

interface GlobalSearchProps {
  className?: string;
}

type SearchResult =
  | { type: 'incident'; data: EnrichedIncident }
  | { type: 'tenant'; data: Tenant }
  | { type: 'action'; label: string; action: () => void };

/**
 * Global command palette search (Cmd+K / Ctrl+K)
 */
export function GlobalSearch({ className }: GlobalSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<number | null>(null);
  const navigate = useNavigate();

  // Default actions when no query
  const defaultActions: SearchResult[] = useMemo(() => [
    { type: 'action', label: 'Go to Dashboard', action: () => navigate('/') },
    { type: 'action', label: 'View All Incidents', action: () => navigate('/incidents') },
    { type: 'action', label: 'View Tenants', action: () => navigate('/tenants') },
    { type: 'action', label: 'Settings', action: () => navigate('/settings') },
  ], [navigate]);

  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      // Also close on Escape when open
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    // Use setTimeout to prevent immediate close from the click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 10);
      setQuery('');
      setSelectedIndex(0);
      setResults(defaultActions);
    }
  }, [isOpen, defaultActions]);

  // Debounced search
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults(defaultActions);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const searchLower = searchQuery.toLowerCase();
    const searchResults: SearchResult[] = [];

    try {
      // Fetch incidents and tenants in parallel
      const [incidentsResult, tenantsResult] = await Promise.all([
        getIncidents({ search: searchQuery }),
        getTenants(),
      ]);

      // Filter and add matching incidents
      if (incidentsResult.success) {
        const matchingIncidents = incidentsResult.data
          .filter(inc =>
            inc.title.toLowerCase().includes(searchLower) ||
            inc.incidentId.toLowerCase().includes(searchLower) ||
            inc.summary?.toLowerCase().includes(searchLower) ||
            inc.aiEnrichment?.tags?.some(tag => tag.toLowerCase().includes(searchLower))
          )
          .slice(0, 5);

        matchingIncidents.forEach(inc => {
          searchResults.push({ type: 'incident', data: inc });
        });
      }

      // Filter and add matching tenants
      if (tenantsResult.success) {
        const matchingTenants = tenantsResult.data
          .filter(t =>
            t.displayName.toLowerCase().includes(searchLower) ||
            t.tenantId.toLowerCase().includes(searchLower)
          )
          .slice(0, 3);

        matchingTenants.forEach(t => {
          searchResults.push({ type: 'tenant', data: t });
        });
      }

      setResults(searchResults.length > 0 ? searchResults : []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [defaultActions]);

  // Handle query changes with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setResults(defaultActions);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = window.setTimeout(() => {
      performSearch(query);
    }, 200);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, performSearch, defaultActions]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter': {
        e.preventDefault();
        const selected = results[selectedIndex];
        if (selected) {
          handleSelect(selected);
        }
        break;
      }
      case 'Escape':
        setIsOpen(false);
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, selectedIndex]);

  const handleSelect = useCallback((result: SearchResult) => {
    setIsOpen(false);
    switch (result.type) {
      case 'incident':
        navigate(`/incidents/${result.data.tenantId}/${result.data.incidentId}`);
        break;
      case 'tenant':
        navigate(`/tenants/${result.data.tenantId}`);
        break;
      case 'action':
        result.action();
        break;
    }
  }, [navigate]);

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {/* Search trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'flex items-center gap-2 px-3 md:px-4 py-2 text-sm text-text-secondary',
          'bg-bg-tertiary rounded-lg border border-border-default',
          'hover:bg-bg-secondary hover:border-border-default transition-colors',
          'min-h-11 md:min-h-0 min-w-0 md:min-w-[280px]'
        )}
        aria-label="Search"
      >
        <Search className="w-4 h-4" />
        <span className="hidden md:block flex-1 text-left">Search incidents, tenants, or type a command...</span>
        <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium bg-bg-secondary rounded">
          <span className="text-xs">&#x2318;</span>K
        </kbd>
      </button>

      {/* Search dropdown - positioned absolutely below the button */}
      {isOpen && (
        <>
          {/* Light backdrop for focus */}
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setIsOpen(false)}
          />

          {/* Search dialog - dropdown style */}
          <div className="fixed inset-x-3 top-16 md:absolute md:inset-x-auto md:top-full md:right-0 md:mt-2 md:w-[500px] md:max-w-[calc(100vw-2rem)] bg-bg-primary rounded-xl shadow-2xl overflow-hidden z-50 border border-border-default">
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border-default">
              <Search className="w-5 h-5 text-text-muted" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Search incidents, tenants, or type a command..."
                className="flex-1 bg-transparent text-text-primary placeholder-text-muted focus:outline-none"
              />
              {isSearching && (
                <div className="w-4 h-4 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-bg-tertiary transition-colors"
              >
                <X className="w-4 h-4 text-text-muted" />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[50vh] overflow-y-auto py-2">
              {results.length === 0 && !isSearching ? (
                <div className="px-4 py-8 text-center text-text-secondary">
                  No results found for "{query}"
                </div>
              ) : (
                <div>
                  {results.map((result, index) => (
                    <SearchResultItem
                      key={index}
                      result={result}
                      isSelected={index === selectedIndex}
                      onSelect={() => handleSelect(result)}
                      onHover={() => setSelectedIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-border-default bg-bg-tertiary/50">
              <div className="flex items-center gap-4 text-xs text-text-secondary">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-bg-secondary rounded">&#x2191;&#x2193;</kbd>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-bg-secondary rounded">&#x21B5;</kbd>
                  select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-bg-secondary rounded">esc</kbd>
                  close
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// SEARCH RESULT ITEM
// ============================================================================

interface SearchResultItemProps {
  result: SearchResult;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

function SearchResultItem({ result, isSelected, onSelect, onHover }: SearchResultItemProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-accent-red/15 text-accent-red';
      case 'high': return 'bg-accent-orange/15 text-accent-orange';
      case 'medium': return 'bg-accent-yellow/15 text-accent-yellow';
      case 'low': return 'bg-accent-blue/15 text-accent-blue';
      default: return 'bg-bg-tertiary text-text-secondary';
    }
  };

  return (
    <button
      onClick={onSelect}
      onMouseEnter={onHover}
      className={cn(
        'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors',
        isSelected
          ? 'bg-bg-tertiary'
          : 'hover:bg-bg-tertiary/50'
      )}
    >
      {result.type === 'incident' && (
        <>
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-red/15 flex items-center justify-center">
            <svg className="w-4 h-4 text-accent-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary truncate">
                {result.data.title}
              </span>
              <span className={cn('px-2 py-0.5 text-xs rounded-full', getSeverityColor(result.data.severity))}>
                {result.data.severity}
              </span>
            </div>
            <div className="text-sm text-text-secondary">
              {result.data.incidentId} &bull; {result.data.tenantDisplayName}
            </div>
          </div>
        </>
      )}

      {result.type === 'tenant' && (
        <>
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-blue/15 flex items-center justify-center">
            <svg className="w-4 h-4 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-text-primary">
              {result.data.displayName}
            </div>
            <div className="text-sm text-text-secondary">
              {result.data.tenantId} &bull; {result.data.incidentCount} incidents
            </div>
          </div>
        </>
      )}

      {result.type === 'action' && (
        <>
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-bg-tertiary flex items-center justify-center">
            <svg className="w-4 h-4 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div className="font-medium text-text-primary">
            {result.label}
          </div>
        </>
      )}

      {isSelected && (
        <svg className="w-4 h-4 text-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
}
